// Match scoring + small formatting helpers, ported from the standalone tool.

import type { InquiryStatus, Item, QuoteLine } from 'pages/inquiry-processor/types';
import { detectCat } from 'pages/inquiry-processor/utils/storage';

// 1 = full match (green), 0.5 = partial (yellow), 0 = not available (red),
// null = no quote line yet. Each unmet requirement is a 0.5 penalty.
export function calcScore(item: Item, quote?: QuoteLine): number | null {
  if (!quote || !quote.available || quote.available === 'no') return 0;
  if (quote.available !== 'yes') return null;

  let penalties = 0;
  if (item.brand && !item.brandOS && quote.brand && item.brand.toLowerCase() !== quote.brand.toLowerCase()) penalties++;
  if (item.origin && !item.originOS && quote.origin && item.origin.toLowerCase() !== quote.origin.toLowerCase())
    penalties++;
  const target = parseFloat(item.targetPrice ?? '');
  const price = parseFloat(quote.price ?? '');
  if (!isNaN(target) && !isNaN(price) && price > target) penalties++;

  return Math.max(0, 1 - 0.5 * penalties);
}

// Chakra colorScheme for a score.
export function scoreColor(s: number | null): string {
  if (s === null) return 'gray';
  if (s >= 0.9) return 'green';
  if (s >= 0.5) return 'yellow';
  return 'red';
}

export function groupByCat(items: Item[]): Record<string, Item[]> {
  const g: Record<string, Item[]> = {};
  for (const it of items) {
    const c = it.category || detectCat(it.name);
    if (!g[c]) g[c] = [];
    g[c].push(it);
  }
  return g;
}

export function itemCategory(it: Item): string {
  return it.category || detectCat(it.name);
}

export function qtyStr(it: Item): string {
  return [it.quantity, it.uom].filter(Boolean).join(' ');
}

export const fmt = (ts?: number): string =>
  ts ? new Date(ts).toLocaleDateString('en-AE', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

export const fmtDT = (ts?: number): string =>
  ts
    ? new Date(ts).toLocaleString('en-AE', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

export const capFirst = (s: string): string => {
  s = (s || '').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
};
export const capWords = (s: string): string =>
  (s || '').trim().replace(/\S+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1));

export const STATUS_META: Record<string, { label: string; colorScheme: string }> = {
  new: { label: 'New', colorScheme: 'blue' },
  processing: { label: 'Processing', colorScheme: 'orange' },
  quote_ready: { label: 'Quote Ready', colorScheme: 'cyan' },
  closed_won: { label: 'Closed Won', colorScheme: 'green' },
  closed_lost: { label: 'Closed Lost', colorScheme: 'red' },
  won: { label: 'Closed Won', colorScheme: 'green' },
  lost: { label: 'Closed Lost', colorScheme: 'red' },
};

export function statusMeta(s: InquiryStatus) {
  return STATUS_META[s] || STATUS_META.new;
}

// Build a WhatsApp-friendly text version of one supplier's quote.
export function supQuoteText(
  companyName: string,
  cat: string,
  sup: { credit?: string; at?: number; notes?: string; items: QuoteLine[] },
  items: Item[]
): string {
  const L: string[] = [];
  L.push(`*Quote — ${cat}*`);
  L.push(companyName);
  if (sup.credit) L.push(`Credit: ${sup.credit}`);
  if (sup.at) L.push(`Date: ${fmtDT(sup.at)}`);
  L.push('');
  items.forEach((it) => {
    const q = (sup.items || []).find((x) => x.itemId === it.id) || ({} as QuoteLine);
    const avail =
      q.available === 'yes' ? (q.active === false ? '✓ (inactive)' : '✓') : q.available === 'no' ? '✗ N/A' : '—';
    const specs = [q.brand, q.origin, q.storage].filter(Boolean).join(', ');
    const price = q.price ? `${q.price}${q.uom ? '/' + q.uom : ''} AED` : '';
    let line = `• ${it.name}: ${avail}`;
    if (specs) line += ` — ${specs}`;
    if (price) line += ` — ${price}`;
    L.push(line);
    if (q.notes) L.push(`   _${q.notes}_`);
  });
  if (sup.notes) {
    L.push('');
    L.push(`Note: ${sup.notes}`);
  }
  return L.join('\n');
}
